import api from '../utils/api';

export interface User {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role: 'user' | 'admin' | 'superadmin';
  status: 'active' | 'inactive' | 'suspended';
  tenant_id?: string;
  tenantId?: string;
  preferences?: {
    theme: 'light' | 'dark';
    language: string;
    viewMode: 'grid' | 'list';
    showHidden: boolean;
  };
  storageQuota?: number;
  storageUsed?: number;
  /** Present when listing with includeStorage */
  storageUsedBytes?: number;
  effectiveStorageLimitBytes?: number;
  membershipStorageQuotaBytes?: number | null;
  allocatedStorageQuotaBytes?: number;
}

export interface UsersListResponse {
  users: User[];
  count: number;
  tenantCapBytes?: number | null;
  /** Sum of active explicit membership caps or legacy user caps. */
  tenantAllocatedBytes?: number;
  /** Org cap minus allocated sum; room left to assign to users. */
  tenantAllocatableRemainingBytes?: number | null;
}

export interface UserResponse {
  users: User[];
  pagination: {
    total: number;
    pages: number;
    page: number;
    limit: number;
  };
}

// User API operations
export const userService = {
  // Get all users (admin only)
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: 'active' | 'inactive';
    includeStorage?: boolean;
  }): Promise<UserResponse | UsersListResponse> => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  /** Users in your tenant with per-user storage usage and limits (admin; tenant-scoped on API). */
  getUsersWithStorageSummary: async (): Promise<UsersListResponse> => {
    const response = await api.get('/users', { params: { includeStorage: true } });
    return response.data;
  },

  /**
   * Set per-user storage cap for this user within their tenant (tenant_users.storage_quota).
   * Pass null to clear the override and fall back to user/tenant defaults.
   */
  updateUserTenantStorageQuota: async (
    userId: string,
    storageQuotaBytes: number | null
  ): Promise<{
    message: string;
    user: User;
    tenantStorage: {
      membershipStorageQuotaBytes: number | null;
      effectiveStorageLimitBytes: number;
      storageUsedBytes: number;
      tenantCapBytes: number;
    };
  }> => {
    const response = await api.put(`/users/${userId}/quota`, { storageQuota: storageQuotaBytes });
    return response.data;
  },

  // Get user by ID (admin only)
  getUser: async (userId: string): Promise<User> => {
    const response = await api.get(`/users/${userId}`);
    return response.data.user;
  },

  // Create user (admin only)
  createUser: async (userData: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    role?: 'user' | 'admin';
    storageQuota?: number;
  }): Promise<User> => {
    const response = await api.post('/users', userData);
    return response.data.user;
  },

  // Update user (admin only)
  updateUser: async (userId: string, userData: Partial<User>): Promise<User> => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data.user;
  },

  // Delete user (admin only)
  deleteUser: async (userId: string): Promise<void> => {
    await api.delete(`/users/${userId}`);
  },

  // Get current user profile
  getProfile: async (): Promise<User> => {
    const response = await api.get('/auth/profile');
    return response.data.user;
  },

  // Update current user profile
  updateProfile: async (profileData: Partial<User>): Promise<User> => {
    const response = await api.put('/auth/profile', profileData);
    return response.data.user;
  },

  // Change password
  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> => {
    await api.post('/auth/change-password', data);
  },

  // Request password reset
  forgotPassword: async (email: string): Promise<void> => {
    await api.post('/auth/forgot-password', { email });
  },

  // Reset password with token
  resetPassword: async (data: {
    token: string;
    newPassword: string;
  }): Promise<void> => {
    await api.post('/auth/reset-password', data);
  }
};

export default userService; 